/**
 * GameErrorBoundary — React error boundary that wraps the game view
 * (Game.jsx + the underlying GameBoard). Catches any uncaught rendering
 * exception thrown during the render / lifecycle phase of its children
 * — regardless of cause (undefined board cell, missing piece property,
 * broken 4-way-checkers state shape, etc.) — and swaps in a recoverable
 * fallback screen instead of leaving the user staring at a blank page.
 *
 * Deliberately narrow scope: this boundary ONLY sits around the game
 * view. It does not intercept AI-turn network errors, stall-guard
 * timeouts, or any code path handled by the existing error banner
 * inside Game.jsx — those are async and never reach this boundary.
 *
 * NOTE: Error boundaries MUST be class components in React (getDerived
 * StateFromError / componentDidCatch have no functional-component
 * equivalent as of React 18).
 */
import React from "react";

export default class GameErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error, info) {
    // Surface in the browser console so developers can diagnose;
    // no telemetry uploaded.
    console.error("[GameErrorBoundary] Uncaught render error:", error, info);
  }

  handleReload = () => {
    // Full reload — cheapest way to reset every provider, cache, ref,
    // and websocket in the game tree. Preserves auth (session token
    // lives in localStorage).
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          data-testid="game-error-boundary"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            textAlign: "center",
            background: "rgba(15, 10, 8, 0.92)",
            color: "#f5e9c6",
            fontFamily: "'Pirata One', 'UnifrakturCook', serif",
          }}
        >
          <h1
            data-testid="game-error-boundary-title"
            style={{ fontSize: "2.25rem", marginBottom: "0.75rem", color: "#e8a95d" }}
          >
            Something went wrong displaying this game
          </h1>
          <p
            data-testid="game-error-boundary-message"
            style={{
              maxWidth: "540px",
              fontSize: "1rem",
              opacity: 0.85,
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            The board hit an unexpected rendering error. Your game is safe on
            the server — reloading will re-fetch and re-draw the board.
          </p>
          {this.state.errorMessage && (
            <pre
              data-testid="game-error-boundary-detail"
              style={{
                maxWidth: "540px",
                fontSize: "0.75rem",
                opacity: 0.55,
                marginBottom: "1.5rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
              }}
            >
              {this.state.errorMessage}
            </pre>
          )}
          <button
            data-testid="game-error-boundary-reload"
            onClick={this.handleReload}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1.05rem",
              borderRadius: "9999px",
              border: "2px solid #e8a95d",
              background: "linear-gradient(180deg, #6b3311 0%, #3d1c07 100%)",
              color: "#f5e9c6",
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: "0.05em",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
